import { platformApiFetch } from '@/services/platformApi/client';

export interface SubscriptionData {
  subscribed: boolean;
  tier: string;
  status?: string | null;
  subscription_source?: string | null;
  subscription_start?: string | null;
  subscription_end?: string | null;
}

export interface SubscriberData {
  id?: string;
  user_id: string;
  email: string;
  subscribed: boolean;
  tier: string;
  subscription_source?: string | null;
  subscription_start?: string | null;
  subscription_end?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface SubscriberCreateData {
  id?: string;  // Optional for upsert operations
  user_id: string;
  email: string;
  subscribed: boolean;
  tier: string;
  subscription_source?: string | null;
  subscription_start?: string | null;
  subscription_end?: string | null;
  updated_at?: string;  // Optional for upsert operations
}

/**
 * Get subscription status for a specific user
 */
export async function getUserSubscriptionStatus(userId: string): Promise<SubscriptionData> {
  try {
    return await platformApiFetch<SubscriptionData>(`/v1/subscriptions/status/${userId}`);
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return { subscribed: false, tier: 'free_tier', status: 'free', subscription_source: null };
  }
}

/**
 * Check if a user has an active subscription
 */
export async function hasActiveSubscription(userId: string): Promise<boolean> {
  const status = await getUserSubscriptionStatus(userId);
  return status.subscribed;
}

/**
 * Get the subscription tier for a user
 */
export async function getUserSubscriptionTier(userId: string): Promise<string> {
  return platformApiFetch<string>(`/v1/subscriptions/tier/${userId}`);
}

/**
 * Get full subscriber data for a user
 */
export async function getSubscriberData(userId: string): Promise<SubscriberData | null> {
  return platformApiFetch<SubscriberData | null>(`/v1/subscriptions/subscriber/${userId}`);
}

/**
 * Check if subscriber record exists for a user
 */
export async function getExistingSubscriber(userId: string): Promise<{ id: string } | null> {
  return platformApiFetch<{ id: string } | null>(`/v1/subscriptions/existing/${userId}`);
}

/**
 * Upsert subscriber data
 */
export async function upsertSubscriber(subscriberData: SubscriberCreateData): Promise<void> {
  await platformApiFetch('/v1/subscriptions/upsert', {
    method: 'POST',
    body: JSON.stringify(subscriberData),
  });
}

/**
 * Get all subscribers (admin only)
 */
export async function getAllSubscribers() {
  return platformApiFetch('/v1/subscriptions/all');
}

/**
 * Check if user has a pending payment
 */
export async function checkPendingPayment(userId: string): Promise<boolean> {
  const status = await getUserSubscriptionStatus(userId);
  return status.status === 'requires_action';
}

/**
 * Get all subscriptions for a user ordered by created_at
 */
export async function getUserSubscriptions(userId: string): Promise<SubscriberData[]> {
  return platformApiFetch<SubscriberData[]>(`/v1/subscriptions/user/${userId}`);
}

/**
 * Create a new subscription record for a user
 */
export async function createSubscriptionRecord(userId: string, subscriptionData: {
  email: string;
  subscribed: boolean;
  tier: string;
  subscription_source: string;
}): Promise<void> {
  await platformApiFetch('/v1/subscriptions/create-record', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      email: subscriptionData.email,
      subscribed: subscriptionData.subscribed,
      tier: subscriptionData.tier,
      subscription_source: subscriptionData.subscription_source,
    }),
  });
}
